import { createClient, SupabaseClient } from '@supabase/supabase-js'

// These will be set via environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

// Only create client if both URL and key are provided
let supabase: SupabaseClient | null = null
if (supabaseUrl && supabaseAnonKey) {
  try {
    supabase = createClient(supabaseUrl, supabaseAnonKey)
  } catch (e) {
    console.warn('Failed to create Supabase client:', e)
  }
}

export { supabase }

// Types for the database
export type DbGuest = {
  name: string
  meal: 'salmon' | 'beef' | 'vegetarian' | 'vegan' | ''
  allergies: string
  is_attending: boolean
  is_plus_one: boolean
}

export type DbRSVP = {
  id?: number
  group_id: string
  guests: DbGuest[]
  not_attending: string[]
  song_request: string
  submitted_at: string
  updated_at?: string
}

// Check if Supabase is configured
export const isSupabaseConfigured = () => {
  return supabase !== null
}

// RSVP operations
export const rsvpService = {
  async getAll(): Promise<DbRSVP[]> {
    if (!supabase) return []
    
    const { data, error } = await supabase
      .from('rsvps')
      .select('*')
      .order('submitted_at', { ascending: false })
    
    if (error) {
      console.error('Error fetching RSVPs:', error)
      return []
    }
    return data || []
  },

  async getByGroupId(groupId: string): Promise<DbRSVP | null> {
    if (!supabase) return null
    
    const { data, error } = await supabase
      .from('rsvps')
      .select('*')
      .eq('group_id', groupId)
      .single()
    
    if (error) {
      if (error.code === 'PGRST116') return null // Not found
      console.error('Error fetching RSVP:', error)
      return null
    }
    return data
  },

  async upsert(rsvp: DbRSVP): Promise<DbRSVP | null> {
    if (!supabase) return null
    
    const { data, error } = await supabase
      .from('rsvps')
      .upsert({
        group_id: rsvp.group_id,
        guests: rsvp.guests,
        not_attending: rsvp.not_attending,
        song_request: rsvp.song_request,
        submitted_at: rsvp.submitted_at,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'group_id'
      })
      .select()
      .single()
    
    if (error) {
      console.error('Error saving RSVP:', error)
      throw new Error(`Failed to save RSVP: ${error.message}`)
    }
    return data
  },

  async delete(groupId: string): Promise<boolean> {
    if (!supabase) return false
    
    const { error } = await supabase
      .from('rsvps')
      .delete()
      .eq('group_id', groupId)
    
    if (error) {
      console.error('Error deleting RSVP:', error)
      return false
    }
    return true
  }
}
